import { createId } from "@paralleldrive/cuid2";
import { create } from "zustand";

import {
  ResumeIntakeEvent,
  ResumeIntakeEventSource,
  TrackedResumeIntakeEvent,
} from "../_types/resumeIntake";

type ResumeIntakeTranscriptState = {
  events: TrackedResumeIntakeEvent[];
};

type ResumeIntakeTranscriptActions = {
  appendEvent: (event: ResumeIntakeEvent, source?: ResumeIntakeEventSource) => void;
  appendAssistantMessage: (content: string, source?: ResumeIntakeEventSource) => void;
  appendUserMessage: (content: string, source?: ResumeIntakeEventSource) => void;
  trackPromptShown: (promptId: string, source?: ResumeIntakeEventSource) => void;
  trackPromptSubmitted: (
    promptId: string,
    value: unknown,
    source?: ResumeIntakeEventSource,
  ) => void;
  trackStateUpdated: (path: string, value: unknown, source?: ResumeIntakeEventSource) => void;
  trackToolStarted: (name: string, source?: ResumeIntakeEventSource) => void;
  trackToolFinished: (name: string, source?: ResumeIntakeEventSource) => void;
  replaceEvents: (events: TrackedResumeIntakeEvent[]) => void;
  clearEvents: () => void;
};

export type ResumeIntakeTranscriptStore = ResumeIntakeTranscriptState &
  ResumeIntakeTranscriptActions;

const createTrackedEvent = (
  event: ResumeIntakeEvent,
  source: ResumeIntakeEventSource = "system",
): TrackedResumeIntakeEvent => ({
  ...event,
  id: createId(),
  createdAt: new Date().toISOString(),
  source,
});

export const useResumeIntakeTranscriptStore = create<ResumeIntakeTranscriptStore>()((set) => ({
  events: [],

  appendEvent: (event, source = "system") =>
    set((state) => ({
      events: [...state.events, createTrackedEvent(event, source)],
    })),

  appendAssistantMessage: (content, source = "system") =>
    set((state) => ({
      events: [...state.events, createTrackedEvent({ type: "assistant_message", content }, source)],
    })),

  appendUserMessage: (content, source = "system") =>
    set((state) => ({
      events: [...state.events, createTrackedEvent({ type: "user_message", content }, source)],
    })),

  trackPromptShown: (promptId, source = "system") =>
    set((state) => ({
      events: [...state.events, createTrackedEvent({ type: "prompt_shown", promptId }, source)],
    })),

  trackPromptSubmitted: (promptId, value, source = "system") =>
    set((state) => ({
      events: [
        ...state.events,
        createTrackedEvent({ type: "prompt_submitted", promptId, value }, source),
      ],
    })),

  trackStateUpdated: (path, value, source = "system") =>
    set((state) => ({
      events: [...state.events, createTrackedEvent({ type: "state_updated", path, value }, source)],
    })),

  trackToolStarted: (name, source = "system") =>
    set((state) => ({
      events: [...state.events, createTrackedEvent({ type: "tool_started", name }, source)],
    })),

  trackToolFinished: (name, source = "system") =>
    set((state) => ({
      events: [...state.events, createTrackedEvent({ type: "tool_finished", name }, source)],
    })),

  replaceEvents: (events) => set({ events }),
  clearEvents: () => set({ events: [] }),
}));

export const getResumeIntakeTranscriptMessages = (events: TrackedResumeIntakeEvent[]) =>
  events.filter((event) => event.type === "assistant_message" || event.type === "user_message");

